import { useState } from "react";
import { useTranslation } from "react-i18next";
import verifiedIcon from "../../../assets/images/icons/check-broken.svg";
import folder from "../../../assets/images/icons/folder.svg";
import pendingIcon from "../../../assets/images/icons/pending.svg";
import rejected from "../../../assets/images/icons/x-red.svg";
import {
  Dropdown,
  FilePreview,
  GeneralButton,
  Modal,
} from "../../../components";
import { EndPoints } from "../../../Constants/endpoints";
import { useGetData, usePostData } from "../../../hooks";
import { GetSpecialtyResponse } from "../../../interface/GeneralResponse";
import { formatDateTimeToShow } from "../../../utils/utils";
import menu from "../../../assets/images/icons/menu.svg"; 
import replace from "../../../assets/images/icons/replace.svg";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "../../../Services/ToastContext";
interface SpecialtyCardProps {
  item?: GetSpecialtyResponse;
  replaceClick?: () => void;
}

const SpecialtyCard: React.FC<SpecialtyCardProps> = ({
  item,
  replaceClick,
}) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isFileOpen, setIsFileOpen] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isGetFileEnabled, setIsGetFileEnabled] = useState(false);

  const { t } = useTranslation();

  const { data, isLoading } = useGetData<{ FileContent?: string }>(
    `${EndPoints.GetSpecialtyDoc}/${(item?.Image ?? 0 > 0) ? item?.Image : item?.PDFContent}`,
    {
      enabled: isGetFileEnabled,
    },
  );

  const handleReasonClick = () => {
    if (item?.Status === false) {
      setIsModalOpen(true);
    }
  };

  const { mutate: mutateDelete } = usePostData(
    `${EndPoints.DeleteSpecialty}/${item?.Specialty?.Id}`,
  );

  const handleDeleteNotification = () => {
    try {
      mutateDelete(
        {},
        {
          onSuccess: () => {
            showToast(t("deleted_successfully"), "success");
            queryClient.refetchQueries({
              queryKey: [EndPoints.GetSpecialty],
              exact: false,
            });
            setIsModalOpen(false);
          },
          onError: (error) => {
            showToast(error.message, "error");
          },
        },
      );
    } catch (error) {
      showToast(t("failed_to_delete"), "error");
    }
  };

  const operations: any[] = [
    {
      icon: replace,
      label: t("replace"),
      onClick: (event: any) => {
        event?.preventDefault();
        event?.stopPropagation();
        setIsModalOpen(false);
        if (replaceClick) {
          replaceClick();
        }
      },
      className: `!w-60 items-center gap-2 rounded-md py-3 px-2`,
    },
    // {
    //   icon: trash,
    //   label: t("delete"),
    //   onClick: (event: any) => {
    //      event?.preventDefault();
    //     event?.stopPropagation();
    //     handleDeleteNotification();
    //   },
    //   className: `!w-60 text-error items-center gap-2 rounded-md py-3 px-2`,
    // },
  ];

  return (
    <>
      <div
        className={`main_warper_without_flex w-full rounded-lg ${(item?.Image || item?.PDFContent) && "cursor-pointer"}`}
        onClick={() => {
          setIsFileOpen(true);
          setIsGetFileEnabled(true);
        }}
      >
        <div className="flex items-center justify-between text-sm">
          <span className="text-base font-bold">
            {item?.Specialty.ParentSpecialty?.Name &&
              item?.Specialty.ParentSpecialty?.Name + " - "}
            {item?.Specialty.Name}
          </span>
          {item?.Status == null ? (
            <div className={`flex min-w-24 items-center gap-2 text-orange`}>
              <img src={pendingIcon} alt="pending" />
              <span className="text-xs font-bold">{t("pending")}</span>
            </div>
          ) : (
            <div
              className={`flex min-w-24 items-center gap-2 ${item?.Status ? "text-primary" : "text-error"}`}
            >
              <img
                src={item?.Status ? verifiedIcon : rejected}
                alt={item?.Status ? "verified" : "rejected"}
              />
              <span className="text-xs font-bold">
                {item?.Status ? t("verified_specialty") : t("rejected")}
              </span>
            </div>
          )}
        </div>
        <div className="mt-3 flex min-w-24 items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="flex h-11 w-11 items-center justify-center rounded-lg border border-primary-40 bg-primary-click">
              <img src={folder} alt="certificate" className="h-7 w-7" />
            </div>
            <div>
              <p className="text-sm">{item?.Specialty.Name}</p>
              <p className="text-xs text-body-text">
                {formatDateTimeToShow(item?.Date ?? "")}
              </p>
            </div>
          </div>
          {item?.Status === false ? (
            <div
              onClick={(event) => {
                event?.preventDefault();
                event?.stopPropagation();
                setIsGetFileEnabled(true);
                handleReasonClick();
              }}
              className="flex cursor-pointer items-center gap-2 rounded-[4px] bg-error px-4 py-1 text-xs font-bold text-white"
            >
              <span>{t("show_details")}</span>
            </div>
          ) : (
            item?.Status !==true ?<Dropdown
              menuItems={operations}
              children={
                <button
                  className="flex h-7 w-7 items-center justify-center rounded-md bg-white"
                  onClick={(event) => {
                    event?.preventDefault();
                    event?.stopPropagation();
                    setIsOpen(!isOpen);
                  }}
                >
                  <img src={menu} alt="menu" className="h-5 w-5" />
                </button>
              }
              isOpen={isOpen}
              setIsOpen={setIsOpen}
            />:<></>
          )}
        </div>
      </div>
      <Modal
        header={t("reject_reason")}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      >
        <p className="mb-5">{item?.Reason}</p>
        <div className="mt-auto flex items-center justify-end gap-2">
          <GeneralButton
            color="red"
            type="button"
            title={t("delete_document")}
            onClick={handleDeleteNotification}
          />
          <GeneralButton
            type="button"
            className="!px-4"
            title={t("try_again")}
            onClick={() => {
              setIsModalOpen(false);
              if (replaceClick) {
                replaceClick();
              }
            }}
          />
        </div>
      </Modal>

      {isFileOpen && (
        <Modal
          header={`${item?.Specialty.ParentSpecialty?.Name ? item?.Specialty.ParentSpecialty?.Name + " - " : ""}
            ${item?.Specialty.Name}`} 
          isOpen={isFileOpen}
          onClose={() => setIsFileOpen(false)}
        >
          <FilePreview
            isLoading={isLoading}
            type={(item?.Image ?? 0 > 0) ? "IMAGE" : "PDF"}
            file={data?.Data?.FileContent}
          />
        </Modal>
      )}
    </>
  );
};

export default SpecialtyCard;
